import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { SUPPORTED_LANGS } from '@/hooks/useLang'
import { Flag } from '@/components/Flag'

const languages = [
  { code: 'en', name: 'English', countryCode: 'US' },
  { code: 'es', name: 'Español', countryCode: 'ES' },
  { code: 'pt', name: 'Português', countryCode: 'BR' },
  { code: 'zh', name: '中文', countryCode: 'CN' },
  { code: 'hi', name: 'हिन्दी', countryCode: 'IN' },
  { code: 'tl', name: 'Filipino', countryCode: 'PH' },
  { code: 'fr', name: 'Français', countryCode: 'FR' },
]

interface LanguageSwitcherProps {
  className?: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'default' | 'sm' | 'lg'
}

export function LanguageSwitcher({
  className,
  variant = 'ghost',
  size = 'default'
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: s => s.location.pathname })

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]

  const changeLanguage = (languageCode: string) => {
    // Save as explicit user preference so the root redirect uses it in future
    localStorage.setItem('phonesalad-language', languageCode)
    const parts = pathname.split('/')
    // parts[0] is '' (before leading slash), parts[1] is the lang segment
    if (parts[1] && SUPPORTED_LANGS.includes(parts[1] as typeof SUPPORTED_LANGS[number])) {
      parts[1] = languageCode
      navigate({ to: parts.join('/') as any, replace: true })
    } else {
      navigate({ to: `/${languageCode}` as any, replace: true })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(
            "flex items-center gap-2",
            className
          )}
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline-flex items-center gap-2">
            <Flag countryCode={currentLanguage.countryCode} size="sm" />
            <span>{currentLanguage.name}</span>
          </span>
          <span className="sm:hidden">
            <Flag countryCode={currentLanguage.countryCode} size="sm" />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            className={cn(
              "flex items-center gap-3 cursor-pointer",
              i18n.language === language.code && "bg-accent"
            )}
          >
            <Flag countryCode={language.countryCode} size="sm" />
            <span>{language.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default LanguageSwitcher
